import { marketLabel } from "../lib/format.js";
import { specLabel } from "../i18n/index.js";
import { useMemo, useState } from "react";
import { useApp } from "../app/AppContext.jsx";
import { narrowModels } from "../lib/catalogue.js";

function Group({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fgroup">
      <button className="fgroup__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="fgroup__title">{title}</span>
        <span className="qgroup__chev" style={{ transform: open ? "rotate(90deg)" : "none" }}>▶</span>
      </button>
      {open && <div className="fgroup__body">{children}</div>}
    </div>
  );
}

/**
 * Facet list with a "show more" cut-off and, for the long dimensions, a search box.
 *
 * Forty makes in a sidebar is unusable; three hundred models is worse. The cut-off handles
 * the first case, but a list you can only page through does not help a buyer who already
 * knows they want a Countach — so the long facets get a filter field, and typing into it
 * searches the whole list, not the visible ten.
 */
function FacetList({ options, selected, onToggle, limit = 8, labelFor, searchable = false }) {
  const { t } = useApp();
  const [all, setAll] = useState(false);
  const [term, setTerm] = useState("");
  const sel = new Set(selected);
  const label = (v) => String(labelFor ? labelFor(v) : v);

  const ordered = useMemo(() => {
    const q = term.trim().toLowerCase();
    const hits = q ? options.filter((o) => label(o.value).toLowerCase().includes(q)) : options;
    // Selected values always render, even below the cut-off, so a filter can always be undone.
    return [...hits.filter((o) => sel.has(o.value)), ...hits.filter((o) => !sel.has(o.value))];
  }, [options, term, selected.join(",")]);

  const shown = all ? ordered : ordered.slice(0, limit);

  return (
    <>
      {searchable && options.length > limit && (
        <input
          className="input fopt__search" type="search" value={term}
          placeholder={t("stock.f.search")} aria-label={t("stock.f.search")}
          onChange={(e) => setTerm(e.target.value)}
        />
      )}

      {/* Expanded, a model list runs to hundreds of entries. It scrolls in place rather than
          pushing the price and year filters below the fold of a page it shares with them. */}
      <div className={`fopts${shown.length > 14 ? " fopts--scroll" : ""}`}>
        {shown.map((o) => (
          <label key={o.value} className="fopt">
            <input type="checkbox" checked={sel.has(o.value)} onChange={() => onToggle(o.value)} />
            <span className="fopt__label">{label(o.value)}</span>
            <span className="fopt__n">{o.n}</span>
          </label>
        ))}

        {shown.length === 0 && <p className="tiny muted">{t("stock.f.noMatch")}</p>}
      </div>

      {ordered.length > limit && (
        <button className="tiny muted" style={{ background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: 0, marginTop: 2 }} onClick={() => setAll((a) => !a)}>
          {all ? t("common.showLess") : `${t("common.showMore")} (${ordered.length - limit})`}
        </button>
      )}
    </>
  );
}

export default function Filters({ query, facets, catalogue, onChange, onReset }) {
  const { t, lang } = useApp();

  const toggleMulti = (key, value) => {
    const cur = query[key] ? String(query[key]).split(",") : [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    onChange({ ...query, [key]: next.join(","), page: 1 });
  };

  const toggleMake = (value) => {
    const cur = query.make ? query.make.split(",") : [];
    const makes = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    const models = narrowModels(query.model ? query.model.split(",") : [], makes, catalogue);
    onChange({ ...query, make: makes.join(","), model: models.join(","), page: 1 });
  };

  const setVal = (key, value) => onChange({ ...query, [key]: value, page: 1 });

  const f = facets || {};


  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span className="label">{t("stock.filters")}</span>
        <button className="tiny muted" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={onReset}>
          {t("stock.clearAll")}
        </button>
      </div>

      <Group title={t("stock.f.make")}>
        <FacetList options={f.make || []} selected={query.make ? query.make.split(",") : []} onToggle={toggleMake} limit={10} searchable />
      </Group>

      {/* Models are counted against the current make filter, so this list is a shortlist of
          Porsches the moment Porsche is ticked, and the whole catalogue's models before
          that. Either way it is the same control, which is why it is not hidden until a
          make is chosen: a buyer who wants a Huracán does not care who makes it. */}
      {f.model?.length > 0 && (
        <Group title={t("stock.f.model")}>
          <FacetList options={f.model} selected={query.model ? query.model.split(",") : []} onToggle={(v) => toggleMulti("model", v)} limit={10} searchable />
        </Group>
      )}

      <Group title={t("stock.f.source")}>
        <FacetList options={f.source || []} selected={query.source ? query.source.split(",") : []} onToggle={(v) => toggleMulti("source", v)} limit={5} labelFor={(v) => marketLabel(v, t)} />
      </Group>

      <Group title={t("stock.f.price")}>
        <div className="range-row">
          <input className="input" type="number" inputMode="numeric" placeholder="0" value={query.priceMin || ""} onChange={(e) => setVal("priceMin", e.target.value)} aria-label={t("common.from")} />
          <span className="muted small">–</span>
          <input className="input" type="number" inputMode="numeric" placeholder="∞" value={query.priceMax || ""} onChange={(e) => setVal("priceMax", e.target.value)} aria-label={t("common.to")} />
        </div>
        <span className="tiny muted">{t("stock.sourcePrice")}</span>
      </Group>

      <Group title={t("stock.f.year")}>
        <div className="range-row">
          <input className="input" type="number" inputMode="numeric" placeholder="1980" value={query.yearMin || ""} onChange={(e) => setVal("yearMin", e.target.value)} aria-label={t("common.from")} />
          <span className="muted small">–</span>
          <input className="input" type="number" inputMode="numeric" placeholder={String(new Date().getFullYear())} value={query.yearMax || ""} onChange={(e) => setVal("yearMax", e.target.value)} aria-label={t("common.to")} />
        </div>
      </Group>

      <Group title={t("stock.f.mileage")} defaultOpen={false}>
        <input className="input" type="number" inputMode="numeric" aria-label={t("stock.f.mileage")} placeholder="km" value={query.mileageMax || ""} onChange={(e) => setVal("mileageMax", e.target.value)} />
      </Group>

      <Group title={t("stock.f.steering")} defaultOpen={false}>
        {["LHD", "RHD", "any"].map((s) => (
          <label key={s} className="fopt">
            <input type="radio" name="steering" checked={(query.steering ?? "LHD") === s} onChange={() => setVal("steering", s)} />
            <span className="fopt__label">{t(`stock.steering.${s}`)}</span>
          </label>
        ))}
      </Group>

      {f.fuel?.length > 0 && (
        <Group title={t("stock.f.fuel")} defaultOpen={false}>
          <FacetList options={f.fuel} selected={query.fuel ? query.fuel.split(",") : []} onToggle={(v) => toggleMulti("fuel", v)} labelFor={(v) => specLabel(lang, "fuel", v)} limit={6} />
        </Group>
      )}

      {f.body?.length > 0 && (
        <Group title={t("stock.f.body")} defaultOpen={false}>
          <FacetList options={f.body} selected={query.body ? query.body.split(",") : []} onToggle={(v) => toggleMulti("body", v)} labelFor={(v) => specLabel(lang, "body", v)} limit={6} />
        </Group>
      )}

      {f.transmission?.length > 0 && (
        <Group title={t("stock.f.transmission")} defaultOpen={false}>
          <FacetList options={f.transmission} selected={query.transmission ? query.transmission.split(",") : []} onToggle={(v) => toggleMulti("transmission", v)} labelFor={(v) => specLabel(lang, "transmission", v)} limit={5} />
        </Group>
      )}

      <div className="fgroup">
        <label className="fopt">
          <input type="checkbox" checked={query.withPhotos === "1"} onChange={(e) => setVal("withPhotos", e.target.checked ? "1" : "")} />
          <span className="fopt__label">{t("stock.f.photos")}</span>
        </label>
      </div>
    </div>
  );
}
